// A record's detail panel. The header holds what decides whether to look further (buyer, title, due date, who
// can bid, the sole-source and eligibility warnings) and the actions (Pursue, Watch, Track); tabs below split
// the rest: an overview, the competition (who holds it, who won, the records around it), the money behind it,
// and its files. Share copies a team or public link (ShareMenu). Leaving records out is the feed editor's job.
// A public shared page (SharedRecord) shows the same tabs and sections, with nothing to open beside them (Opens).
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type KeyboardEvent, type ReactNode } from "react";
import { api, errorText } from "../api/client";
import type { Incumbent, NoticeDetail, PublicRecord, Winner } from "../api/types";
import { useUI, type PanelTarget, type ViewerDoc } from "../app/context";
import { keys, useFeeds, useRefreshLists } from "../app/queries";
import { Badge } from "../components/Badge";
import { Highlight } from "../components/Highlight";
import { ShareMenu } from "../components/ShareMenu";
import { eventWhen, FollowHost, SoleChip } from "../components/Rows";
import { changeWords, cleanDesc, companyName, days, fmt, lineRef, links, money, orgName, sameCompany, shortDate, titleCase } from "../lib/format";

export type Tab = "overview" | "competition" | "money" | "files";
const TAB_NAMES: Record<Tab, string> = { overview: "Overview", competition: "Competition", money: "Money", files: "Files" };
// The tab stays put as j/k moves between records, when the next record has it.
let lastTab: Tab = "overview";

/** What a record's sections can open. The panel opens other records, budget lines and documents beside it, and
 * follows event hosts; a public page has none of that, so its rows read as plain text and documents open at their
 * source. */
export interface Opens {
  record?: (key: string) => void;
  budget?: (id: string) => void;
  doc?: (page?: number | null) => void;
  viewer?: (doc: ViewerDoc) => void;
  copy?: (value: string) => void;
  follow?: boolean;
}

const date = (iso: string | null | undefined, year = true) =>
  fmt(iso?.slice(0, 10), year ? { month: "short", day: "numeric", year: "numeric" } : { month: "short", day: "numeric" });

export function NoticePanel({ target }: { target: Extract<PanelTarget, { kind: "notice" }> }) {
  const { key, feedId } = target;
  const { closePanel, openPanel, showToast, viewer, openViewer } = useUI();
  const feeds = useFeeds().data?.feeds ?? [];
  const feed = feedId ? (feeds.find((f) => f.id === feedId) ?? null) : null;
  const client = useQueryClient();
  const refresh = useRefreshLists();
  const [picked, setPicked] = useState<Tab>(lastTab);
  const detail = useQuery({
    queryKey: keys.notice(key, feedId),
    queryFn: () => api<NoticeDetail>(`/api/notices/${encodeURIComponent(key)}${feedId ? `?feed=${feedId}` : ""}`),
  });
  const n = detail.data;
  const files = useQuery({
    queryKey: ["notice", key, "files"],
    queryFn: () => api<{ files: NoticeFile[] }>(`/api/notices/${encodeURIComponent(key)}/files`).then((d) => d.files),
    staleTime: 10 * 60_000,
  });
  const why = n?.why;
  const cite = {
    terms: why?.reasons.flatMap((r) => r.terms ?? []) ?? [],
    quote: why?.reasons.find((r) => r.quote)?.quote ?? "",
  };
  // The viewer follows the selection: another record's document replaces it, or it closes.
  useEffect(() => {
    if (!n || !viewer) return;
    if (n.document) openViewer({ ...n.document, ...cite });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n?.key]);

  if (detail.isPending)
    return (
      <div className="p-head">
        <p className="empty">Loading…</p>
      </div>
    );
  if (detail.isError || !n)
    return (
      <div className="p-head">
        <div className="p-top">
          <span />
          <button className="x" aria-label="Close" onClick={closePanel}>
            ×
          </button>
        </div>
        <p className="empty">{errorText(detail.error)}</p>
      </div>
    );

  const up = n.horizon === "upcoming";
  const org = orgName(n.agency);
  const docs = otherLinks(n, files.data ?? []);
  const tabs = tabsFor(n, (files.data?.length ?? 0) + docs.length);
  const tab = tabs.includes(picked) ? picked : "overview";
  const go = (t: Tab) => {
    lastTab = t;
    setPicked(t);
    document.getElementById("p-body")?.scrollTo({ top: 0 });
  };

  const mark = async (decision: "pursue" | "watch") => {
    const next = n.mark === decision ? null : decision;
    const r = await api<{ tracking_started: boolean }>(`/api/feeds/${feed!.id}/marks`, { method: "POST", body: { key, decision: next } });
    // Say so now: a toast that waited for every list to reload could land after (and replace) a later one.
    client.invalidateQueries({ queryKey: ["notice", key] });
    refresh();
    showToast(
      next
        ? `Marked ${next === "pursue" ? "Pursue" : "Watch"}. ${feed!.name} will favor records like this.${r.tracking_started ? " You’ll also get alerts when it changes." : ""}`
        : "Mark removed.",
    );
  };
  const track = async () => {
    try {
      await api(`/api/tracked/${encodeURIComponent(key)}`, { method: n.tracked ? "DELETE" : "PUT" });
      await client.invalidateQueries({ queryKey: ["notice", key] });
      showToast(
        n.tracked
          ? "No longer tracking it."
          : up
            ? "Tracking it. You’ll hear when its expected date moves and when it’s released as a solicitation."
            : "Tracking it. You’ll get alerts when its due date, text, attachments or status change.",
      );
    } catch (err) {
      showToast(errorText(err));
    }
  };
  const opens: Opens = {
    record: (k) => openPanel({ kind: "notice", key: k, feedId: feedId ?? null }),
    budget: (id) => openPanel({ kind: "budget", id, back: target, from: n.title }),
    doc: (page) => n.document && openViewer({ ...n.document, ...cite, page: page ?? n.document.page }),
    viewer: openViewer,
    copy: (v) =>
      navigator.clipboard.writeText(v).then(
        () => showToast(`Copied ${v}.`),
        () => showToast("Couldn’t copy it."),
      ),
    follow: true,
  };

  return (
    <>
      <div className="p-head np-head">
        <div className="p-top">
          <div className="p-org" title={org.full}>
            <Badge agency={n.agency} small />
            <span>{org.short || "Unknown buyer"}</span>
          </div>
          <button className="x" aria-label="Close" onClick={closePanel}>
            ×
          </button>
        </div>
        <h2>{titleCase(n.title)}</h2>
        <div className="np-status">
          <When n={n} />
          <span>{[n.kind, n.who_label || "Open to anyone"].filter(Boolean).join(" · ")}</span>
          {n.sole_source ? <SoleChip /> : null}
          {n.ineligible ? <span className="chip warn">Not eligible: {n.ineligible.toLowerCase()}</span> : null}
        </div>
        <div className="p-actions">
          {feed ? (
            <>
              <button className="btn" data-d="pursue" aria-pressed={n.mark === "pursue"} onClick={() => mark("pursue")}>
                Pursue
              </button>
              <button className="btn" data-d="watch" aria-pressed={n.mark === "watch"} onClick={() => mark("watch")}>
                Watch
              </button>
            </>
          ) : null}
          <button className="btn" id="track" aria-pressed={n.tracked} title={up ? "Alerts when its expected date moves, and when it is released as a solicitation" : "Alerts when the due date, text, attachments or status change"} onClick={track}>
            {n.tracked ? "Tracking" : "Track"}
          </button>
          <ShareMenu key={key} noticeKey={key} />
          <span className="spacer" />
          {/^(?:https?:\/\/|\/api\/ati-files\?)/i.test(n.url) ? (
            n.url_is_list ? (
              // No page of its own at the source: the title goes on the clipboard for the list's search box.
              <a className="btn" href={n.url} target="_blank" rel="noopener noreferrer" title={listHint(n.title)} onClick={() => navigator.clipboard.writeText(n.title).then(() => showToast(`Copied “${titleCase(n.title)}” to paste into the list’s search.`), () => {})}>
                Open the list it’s on ↗
              </a>
            ) : (
              <a className="btn" href={n.url} target="_blank" rel="noopener noreferrer">
                Open original ↗
              </a>
            )
          ) : null}
        </div>
        <TabBar n={n} tabs={tabs} tab={tab} fileCount={(files.data?.length ?? 0) + docs.length} go={go} />
      </div>
      <div className="p-body np-body" id="p-body" role={tabs.length > 1 ? "tabpanel" : undefined} aria-labelledby={tabs.length > 1 ? `tab-${tab}` : undefined}>
        <RecordTab tab={tab} n={n} org={org} cite={cite} go={go} opens={opens} files={files.data ?? []} docs={docs} />
      </div>
    </>
  );
}

/** The record's links other than its files: those named in its text, and saved links the file list lacks. */
export function otherLinks(n: PublicRecord, files: { url: string; original?: string }[]) {
  return links(n).filter(([, url]) => !files.some((f) => f.url === url || f.original === url));
}

/** The tabs a record has: the overview, then competition, money and files when it has any. */
export function tabsFor(n: PublicRecord, fileCount: number): Tab[] {
  return [
    "overview",
    ...(n.incumbent?.current || n.winners.length || n.lineage.length ? (["competition"] as const) : []),
    ...(n.budget.length || n.paid_from.length ? (["money"] as const) : []),
    ...(fileCount ? (["files"] as const) : []),
  ];
}

export function TabBar({ n, tabs, tab, fileCount, go }: { n: PublicRecord; tabs: Tab[]; tab: Tab; fileCount: number; go: (t: Tab) => void }) {
  if (tabs.length < 2) return null;
  const counts: Partial<Record<Tab, number>> = { money: n.budget.length + n.paid_from.length, files: fileCount };
  const onKey = (e: KeyboardEvent) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    const next = tabs[(tabs.indexOf(tab) + (e.key === "ArrowRight" ? 1 : tabs.length - 1)) % tabs.length]!;
    go(next);
    document.getElementById(`tab-${next}`)?.focus();
  };
  return (
    <div className="p-tabs" role="tablist" aria-label="Record sections" onKeyDown={onKey}>
      {tabs.map((t) => (
        <button key={t} id={`tab-${t}`} role="tab" aria-selected={t === tab} aria-controls="p-body" tabIndex={t === tab ? 0 : -1} onClick={() => go(t)}>
          {TAB_NAMES[t]}
          {counts[t] ? <span className="n">{counts[t]}</span> : null}
        </button>
      ))}
    </div>
  );
}

export function RecordTab({
  tab,
  n,
  org,
  cite,
  go,
  opens,
  files,
  docs,
}: {
  tab: Tab;
  n: PublicRecord;
  org: { short: string; full: string };
  cite: { terms: string[] };
  go: (t: Tab) => void;
  opens: Opens;
  files: NoticeFile[];
  docs: [string, string, string][];
}) {
  const inc = n.incumbent?.current ? n.incumbent : null;
  if (tab === "overview") return <Overview n={n} org={org} cite={cite} go={go} opens={opens} />;
  if (tab === "competition")
    return (
      <>
        {inc ? <IncumbentCard inc={inc} /> : null}
        {n.winners.length ? <Winners winners={n.winners} /> : null}
        {n.lineage.length ? <Lineage lineage={n.lineage} open={opens.record} /> : null}
      </>
    );
  if (tab === "money") return <Money n={n} openBudget={opens.budget} />;
  return <Files files={files} docs={docs} />;
}

/** A row that opens another record in the panel, or plain text where nothing can open it (a public page). */
function RecordRow({ open, children }: { open?: () => void; children: ReactNode }) {
  return open ? (
    <button className="lrow" onClick={open}>
      {children}
    </button>
  ) : (
    <div className="lrow">{children}</div>
  );
}

/** Why a record's link opens a list: the source gives it no page of its own. */
export const listHint = (title: string) => `The source has no page for this record alone; search its list for “${titleCase(title)}”.`;

/** A source document's page, at its source: for public pages, where the app's document viewer isn't open to them. */
const atSource = (original: string, page?: number | null) => (page ? `${original}#page=${page}` : original);

/** The due date and how far off it is, or an upcoming record's window, or when a context record was published. */
export function When({ n }: { n: PublicRecord }) {
  if (n.event) {
    const d = n.event.start ? days(n.event.start) : null;
    return (
      <span className={`due ${d !== null && d >= 0 && d <= 7 ? "urgent" : ""}`}>
        {n.event.label} · {eventWhen(n.event)}
        {d !== null && d >= 0 ? ` · ${d === 0 ? "today" : d === 1 ? "tomorrow" : `${d} days`}` : d !== null ? " · held" : ""}
      </span>
    );
  }
  if (n.horizon === "context") return <span className="due est">Published {date(n.published)}</span>;
  if (n.horizon === "withdrawn") return <span className="due est past" title={n.withdrawn ?? ""}>No longer forecast</span>;
  if (n.horizon === "upcoming") {
    const w = n.window;
    if (!w) return <span className="due est">Date not stated</span>;
    return (
      <span className={`due est ${w.basis === "released" ? "released" : w.past ? "past" : ""}`} title={w.text + (w.source_text ? ` (source: “${w.source_text}”)` : "")}>
        Expected {w.label}
      </span>
    );
  }
  const d = days(n.deadline);
  if (d === null) return <span className="due">No due date</span>;
  if (d < 0) return <span className="due">Closed {date(n.deadline)}</span>;
  const left = d === 0 ? "today" : d === 1 ? "tomorrow" : `${d} days`;
  return (
    <span className={`due ${d <= 3 ? "urgent" : ""}`}>
      Due {shortDate(n.deadline, { weekday: "short", month: "short", day: "numeric" })} · {left}
    </span>
  );
}

function Overview({
  n,
  org,
  cite,
  go,
  opens,
}: {
  n: PublicRecord;
  org: { short: string; full: string };
  cite: { terms: string[] };
  go: (t: Tab) => void;
  opens: Opens;
}) {
  const [allPassages, setAllPassages] = useState(false);
  const up = n.horizon === "upcoming";
  const when: [string, string] = up
    ? ["Expected", n.window ? `${n.window.label} · ${n.window.text}` : ""]
    : n.horizon === "withdrawn"
      ? ["Forecast", n.withdrawn ?? "No longer forecast"]
      : n.horizon === "context"
      ? ["Published", fmt(n.published, { month: "long", day: "numeric", year: "numeric" })]
      : ["Due", n.deadline ? fmt(n.deadline, { weekday: "long", month: "long", day: "numeric", year: "numeric" }) : ""];
  const facts = [
    when,
    ...n.planning_facts,
    ["Kind of work", n.work_full ? n.work_full + (n.shape_conflicted ? " (sources disagree)" : "") : "Not stated"],
    ["How it’s bought", n.path_full],
    ["Buys a named product", n.named],
    ["Set-aside", n.set_aside && !/not reported|no set aside/i.test(n.set_aside) ? n.set_aside : ""],
    // The breadcrumb already names the buyer when it spells out the office path.
    ["Buyer", n.buyer && !org.full.toLowerCase().includes(n.buyer.toLowerCase()) ? n.buyer : ""],
    ["Source", n.edition],
  ].filter(([, v]) => v) as [string, string][];

  // One line each for what the other tabs hold, so the overview says whether they're worth opening.
  const inc = n.incumbent?.current;
  const ending = n.lineage.filter((l) => l.role === "Contract ending").length;
  const forecasts = n.lineage.filter((l) => l.role === "Forecast").length;
  const followOns = n.lineage.filter((l) => l.role === "Follow-on");
  const top = n.budget[0];
  const glance = [
    inc
      ? [
          "Held by",
          companyName(inc.vendor) +
            [inc.ceiling ? `${money(inc.ceiling / 1e6)} ceiling` : "", inc.end || inc.completion ? `ends ${date(inc.end || inc.completion)}` : ""]
              .filter(Boolean)
              .map((s) => " · " + s)
              .join(""),
          "competition",
        ]
      : null,
    n.winners[0] ? ["Awarded to", companyName(n.winners[0].vendor) + (n.winners.length > 1 ? ` + ${n.winners.length - 1} more` : ""), "competition"] : null,
    followOns[0] ? ["Follow-on", titleCase(followOns[0].title), "competition"] : null,
    ending || forecasts
      ? [
          "Related",
          [ending ? `${ending} contract${ending === 1 ? "" : "s"} ending` : "", forecasts ? `${forecasts} forecast${forecasts === 1 ? "" : "s"}` : ""]
            .filter(Boolean)
            .join(", "),
          "competition",
        ]
      : null,
    top
      ? [
          "Funded by",
          `${lineRef(top)} · ${top.title}${top.strength === "possible" ? " (possible)" : ""}${n.budget.length > 1 ? ` + ${n.budget.length - 1} more` : ""}`,
          "money",
        ]
      : null,
    n.paid_from[0] ? ["Paid from", n.paid_from[0].account, "money"] : null,
  ].filter(Boolean) as [string, string, Tab][];

  const ref = [
    ["Solicitation", n.solicitation_id],
    ["NAICS", (n.naics_codes ?? []).join(", ")],
    ["PSC", n.psc_code],
    // Unless the source line already says it ("Acquisition Gateway forecast", found on "GSA Acquisition Gateway forecast").
    ["Found on", sameSource(n.source_label, n.edition) ? "" : n.source_label],
  ].filter(([, v]) => v) as [string, string][];
  const passages = allPassages ? n.passages : n.passages.slice(0, 2);
  const text = cleanDesc(n.description);

  return (
    <>
      {glance.length ? (
        <dl className="glance">
          {glance.map(([k, v, t]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd>
                <button className="link-btn" onClick={() => go(t)}>
                  {v}
                </button>
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
      {n.passages.length ? (
        <section>
          <h4>Key passages</h4>
          <ul className="passages">
            {passages.map((p) => (
              <li key={p}>
                <Highlight text={p} terms={cite.terms} as="term" />
              </li>
            ))}
          </ul>
          {n.passages.length > passages.length ? (
            <button className="text-btn more-btn" onClick={() => setAllPassages(true)}>
              {n.passages.length - passages.length} more passage{n.passages.length - passages.length === 1 ? "" : "s"}
            </button>
          ) : null}
        </section>
      ) : null}
      {n.event ? (
        <section>
          <h4>The event</h4>
          <dl className="glance">
            {n.event.host !== "other" ? (
              <div>
                <dt>Held by</dt>
                <dd>
                  {n.event.host_label} {opens.follow ? <FollowHost id={n.event.host} label={n.event.host_label} /> : null}
                </dd>
              </div>
            ) : null}
            <div>
              <dt>When</dt>
              <dd>{eventWhen(n.event)}</dd>
            </div>
            {n.event.location ? (
              <div>
                <dt>Where</dt>
                <dd>{n.event.location}</dd>
              </div>
            ) : null}
            {n.event.registration_closes ? (
              <div>
                <dt>Register by</dt>
                <dd>{fmt(n.event.registration_closes, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}</dd>
              </div>
            ) : null}
          </dl>
        </section>
      ) : null}
      {n.linked_events.length ? (
        <section>
          <h4>{n.event ? "The opportunity" : "Events"}</h4>
          <ul className="lrows">
            {n.linked_events
              // One row a solicitation: SAM.gov's notice and the agency's own copy of it.
              .filter((x, i, all) => !x.solicitation_id || all.findIndex((y) => y.solicitation_id === x.solicitation_id) === i)
              .map((x) => (
                <li key={x.key}>
                  <RecordRow open={opens.record && (() => opens.record!(x.key))}>
                    <span className="role">{x.event ? x.event.label : x.horizon === "upcoming" ? "Forecast" : "Open"}</span>
                    <span className="lrow-main">
                      <b>{titleCase(x.title)}</b>
                      <span className="sub">{[x.buyer, x.solicitation_id].filter(Boolean).join(" · ")}</span>
                    </span>
                    <span className="lrow-side">
                      <span className="sub">{x.event ? eventWhen(x.event) : x.deadline ? `due ${date(x.deadline)}` : ""}</span>
                    </span>
                  </RecordRow>
                </li>
              ))}
          </ul>
        </section>
      ) : null}
      {n.roadmap_pages.length ? (
        <section>
          <h4>In briefings and roadmaps</h4>
          <ul className="lrows roadmaps">
            {n.roadmap_pages.map((r) => (
              <li key={r.key} className="lrow">
                <div className="lrow-main">
                  <b>
                    {r.label}
                    {r.page ? ` · p. ${r.page}` : ""}
                  </b>
                  <span className="sub quote">
                    <Highlight text={r.snippet} terms={r.terms} as="term" />
                  </span>
                  <span className="sub">
                    {r.buyer} · names {r.terms.map((t) => `“${t}”`).join(", ")}
                  </span>
                </div>
                <div className="lrow-side">
                  {r.document && opens.viewer ? (
                    <button className="btn small" onClick={() => opens.viewer!({ ...r.document!, terms: r.terms, quote: "" })}>
                      View page
                    </button>
                  ) : (
                    <a className="btn small" href={r.url} target="_blank" rel="noopener noreferrer">
                      Open ↗
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {n.document ? (
        <section>
          <h4>Source document</h4>
          <div className="source-doc">
            {opens.doc ? (
              <button className="btn v-open" onClick={() => opens.doc!()}>
                {n.document.type === "sheet"
                  ? `View row ${n.document.row ?? ""} in the spreadsheet`
                  : n.document.page
                    ? `View page ${n.document.page}`
                    : "View the document"}
              </button>
            ) : (
              <a
                className="btn"
                href={atSource(n.document.original, n.document.type === "pdf" ? n.document.page : null)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {n.document.type === "sheet"
                  ? `Open the spreadsheet${n.document.row ? ` (row ${n.document.row})` : ""} ↗`
                  : n.document.page
                    ? `Open page ${n.document.page} ↗`
                    : "Open the document ↗"}
              </a>
            )}
            <span className="muted-s">{n.document.label || n.edition}</span>
          </div>
          {n.document.type === "pdf" && n.pages.some((p) => p.page) ? (
            <div className="pages">
              <span>{n.page_total ? `Other matching pages (of ${n.page_total}):` : "Other matching pages:"}</span>
              {n.pages
                .filter((p) => p.page)
                .slice(0, 24)
                .map((p) =>
                  opens.doc ? (
                    <button key={p.key} className="chip" title={p.fit_word ?? ""} onClick={() => opens.doc!(p.page)}>
                      p. {p.page}
                    </button>
                  ) : (
                    <a key={p.key} className="chip" href={atSource(n.document!.original, p.page)} target="_blank" rel="noopener noreferrer">
                      p. {p.page}
                    </a>
                  ),
                )}
            </div>
          ) : null}
        </section>
      ) : null}
      <section>
        <h4>Details</h4>
        <dl className="facts np-facts">
          {facts.map(([k, v]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd title={k === "Kind of work" && n.shape_evidence.length ? "Based on " + n.shape_evidence.join("; ") : undefined}>{v}</dd>
            </div>
          ))}
        </dl>
      </section>
      {ref.length ? (
        <dl className="ref">
          {ref.map(([k, v]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd>
                {k === "Found on" || !opens.copy ? (
                  v
                ) : (
                  <button className="copy" title={`Copy ${v}`} onClick={() => opens.copy!(v)}>
                    {v}
                  </button>
                )}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
      {text ? (
        <details className="full-text">
          <summary>Full notice text</summary>
          <div className="body-text">{text}</div>
        </details>
      ) : (
        <p className="muted-s">No description was published with this record. Open the original for details.</p>
      )}
    </>
  );
}

const sameSource = (a: string, b: string) => !!a && !!b && (a.toLowerCase().includes(b.toLowerCase()) || b.toLowerCase().includes(a.toLowerCase()));

/** Who holds the work now, how that was found, and the sentence that says so. */
function IncumbentCard({ inc }: { inc: Incumbent }) {
  const c = inc.current!;
  const end = c.end || c.completion;
  return (
    <section>
      <h4>Holds it now</h4>
      <div className="fund-card winner">
        <b>
          {companyName(c.vendor)}
          {c.parent && !sameCompany(c.parent, c.vendor) ? <span className="where"> · part of {companyName(c.parent)}</span> : null}
        </b>
        <span className="nums">
          {[
            c.piid ? `${inc.vehicle ? "order" : "contract"} ${c.piid}` : "",
            c.ceiling ? `${money(c.ceiling / 1e6)} ceiling` : "",
            c.obligated_total ? `${money(c.obligated_total / 1e6)} obligated` : "",
            end ? `ends ${date(end)}` : "",
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
        {inc.vehicle ? (
          <span className="where">
            Under {inc.vehicle.acronym} ({inc.vehicle.piid}
            {inc.vehicle.end ? `, runs to ${fmt(inc.vehicle.end.slice(0, 10), { month: "short", year: "numeric" })}` : ""})
            {inc.order_count ? ` · ${inc.order_count} orders from ${inc.office}` : ""}
          </span>
        ) : null}
        <span className="why">“{inc.evidence}”</span>
        {c.url ? (
          <span className="go">
            <a href={c.url} target="_blank" rel="noopener noreferrer">
              USAspending ↗
            </a>
          </span>
        ) : null}
      </div>
    </section>
  );
}

const SOURCE_NAMES: Record<string, string> = {
  fpds: "FPDS",
  sam: "SAM.gov award notice",
  war: "War.gov contracts",
  release: "Press release",
  nih: "NIH RePORTER",
};

/** An award's facts as separate phrases, so the ones every award shares can be said once. */
function winnerFacts(w: Winner) {
  return {
    amount: w.amount != null ? `${money(w.amount / 1e6)}${w.source === "fpds" && w.obligated != null && w.amount !== w.obligated ? " with options" : ""}` : "",
    date: w.date ? `awarded ${date(w.date)}` : "",
    how: [
      w.offers ? `${w.offers} offer${w.offers === "1" ? "" : "s"}` : "",
      w.competition ? titleCase(w.competition).replace(/\.$/, "") : "",
      w.set_aside && !/no set aside/i.test(w.set_aside) ? titleCase(w.set_aside).replace(/\.$/, "") : "",
      w.pricing ? titleCase(w.pricing) : "",
    ]
      .filter(Boolean)
      .join(" · "),
    title: w.title ? titleCase(w.title) : "",
  };
}
type WinnerField = keyof ReturnType<typeof winnerFacts>;

/** The awards, latest first. A multiple-award contract's awardees share their terms: those are stated once above
 * a row per vendor. A standing announcement's hundreds of projects start folded. */
function Winners({ winners }: { winners: Winner[] }) {
  const [all, setAll] = useState(false);
  const facts = winners.map(winnerFacts);
  const fields: WinnerField[] = ["amount", "date", "how", "title"];
  const same = (f: WinnerField, a: string, b: string) => a === b || (f === "title" && alike(a, b));
  const shared = new Set(winners.length > 1 ? fields.filter((f) => facts[0]![f] && facts.every((x) => same(f, x[f], facts[0]![f]))) : []);
  const shown = all ? winners : winners.slice(0, 8);
  const lead = facts[0]!;
  return (
    <section>
      <h4>
        Who won{winners.length > 1 ? ` · ${winners.length} awards` : ""}
      </h4>
      {shared.size ? (
        <div className="group-h">
          <span>{[shared.has("amount") ? `${lead.amount} each` : "", shared.has("date") ? lead.date : "", shared.has("how") ? lead.how : ""].filter(Boolean).join(" · ")}</span>
          {shared.has("title") ? <span className="why">{lead.title}</span> : null}
        </div>
      ) : null}
      <ul className="lrows">
        {shown.map((w, i) => {
          const f = facts[i]!;
          const rest = fields.filter((k) => !shared.has(k) && f[k] && k !== "title").map((k) => f[k]);
          return (
            <li key={w.id} className="lrow">
              <div className="lrow-main">
                <b>
                  {companyName(w.vendor)}
                  {w.strength === "possible" ? <span className="strength possible"> Possible</span> : null}
                </b>
                {w.parent && !sameCompany(w.parent, w.vendor) ? <span className="sub">part of {companyName(w.parent)}</span> : null}
                {rest.length ? <span className="sub">{rest.join(" · ")}</span> : null}
                {w.pi ? <span className="sub">PI {titleCase(w.pi)}</span> : null}
                {w.evidence ? <span className="sub">{w.evidence}</span> : null}
                {!shared.has("title") && f.title ? <span className="sub">{f.title}</span> : null}
              </div>
              <div className="lrow-side">
                {w.piid ? <span className="mono">{w.piid}</span> : null}
                <span className="srcs">
                  {w.sources.map((s, j) => (
                    <a key={j} href={s.url} target="_blank" rel="noopener noreferrer" title={SOURCE_NAMES[s.source] ?? s.source}>
                      {s.source === "sam" ? "SAM" : (SOURCE_NAMES[s.source] ?? s.source)} ↗
                    </a>
                  ))}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
      {winners.length > shown.length ? (
        <button className="text-btn more-btn" onClick={() => setAll(true)}>
          Show all {winners.length}
        </button>
      ) : null}
    </section>
  );
}

/** Titles that differ only in a word or two (one award's "(S2ISR)" or "the"), as a multiple-award contract's
 * awardees' descriptions do. */
function alike(a: string, b: string): boolean {
  const words = (s: string) => new Set(s.toLowerCase().match(/[a-z0-9]{3,}/g) ?? []);
  const x = words(a);
  const y = words(b);
  const both = [...x].filter((w) => y.has(w)).length;
  return both / (x.size + y.size - both || 1) >= 0.75;
}

const ROLE_WORDS: Record<NoticeDetail["lineage"][number]["role"], string> = { "Contract ending": "Ending", Forecast: "Forecast", "Follow-on": "Follow-on" };

/** The records this one follows from or leads to, grouped by what joins them: one contract a dozen records cite
 * reads as one heading, not a dozen repeated sentences. */
function Lineage({ lineage, open }: { lineage: NoticeDetail["lineage"]; open?: (key: string) => void }) {
  const groups = new Map<string, NoticeDetail["lineage"]>();
  for (const l of lineage) groups.set(l.why, [...(groups.get(l.why) ?? []), l]);
  return (
    <section>
      <h4>Related records</h4>
      {[...groups].map(([why, rows]) => (
        <LineageGroup key={why} why={why} rows={rows} open={open} />
      ))}
    </section>
  );
}

function LineageGroup({ why, rows, open }: { why: string; rows: NoticeDetail["lineage"]; open?: (key: string) => void }) {
  const [all, setAll] = useState(false);
  // The longest spelling, which usually keeps the "Inc." the others drop.
  const holder = rows.every((r) => sameCompany(r.incumbent, rows[0]!.incumbent))
    ? companyName(rows.map((r) => r.incumbent).sort((a, b) => b.length - a.length)[0]!)
    : "";
  const shown = all ? rows : rows.slice(0, 5);
  return (
    <div className="lgroup">
      <div className="group-h">
        <span className="why">
          {why.replace(/\.$/, "")}
          {holder ? ` · held by ${holder}` : ""}
        </span>
      </div>
      <ul className="lrows">
        {shown.map((l) => (
          <li key={l.key}>
            <RecordRow open={open && (() => open(l.key))}>
              <span className={`role ${l.role === "Contract ending" ? "ending" : ""}`}>{ROLE_WORDS[l.role]}</span>
              <span className="lrow-main">
                <b>{titleCase(l.title.replace(/\s*\(ends [^)]*\)$/, ""))}</b>
                {!holder && l.incumbent ? <span className="sub">Held by {companyName(l.incumbent)}</span> : null}
              </span>
              <span className="lrow-side">{l.date ? <span className="sub">{(l.role === "Contract ending" ? "ends " : "") + date(l.date)}</span> : null}</span>
            </RecordRow>
          </li>
        ))}
      </ul>
      {rows.length > shown.length ? (
        <button className="text-btn more-btn" onClick={() => setAll(true)}>
          {rows.length - shown.length} more
        </button>
      ) : null}
    </div>
  );
}

function Money({ n, openBudget }: { n: PublicRecord; openBudget?: (id: string) => void }) {
  return (
    <>
      {n.budget.length ? (
        <section>
          <h4>Funded by</h4>
          <div className="funded">
            {n.budget.map((b) => {
              const c = changeWords(b);
              const inside = (
                <>
                  <span className={`strength ${b.strength}`}>{b.strength === "linked" ? "Linked" : "Possible"}</span>
                  <b>{b.title}</b>
                  <span className="where">{b.kind === "cj" ? lineRef(b) : `${b.service} · ${lineRef(b, true)}`}</span>
                  <span className="nums">
                    {money(b.request)} FY{String(b.edition).slice(2)} request {c.text ? <span className={`chg ${c.tone}`}>{c.text}</span> : null}
                  </span>
                  <span className="why">{b.evidence}</span>
                </>
              );
              return openBudget ? (
                <button key={b.id} className="fund-card" onClick={() => openBudget(b.id)}>
                  {inside}
                  <span className="go">Open budget line →</span>
                </button>
              ) : (
                <div key={b.id} className="fund-card">
                  {inside}
                </div>
              );
            })}
          </div>
        </section>
      ) : null}
      {n.paid_from.length ? (
        <section>
          <h4>Current contract paid from</h4>
          <ul className="lrows">
            {n.paid_from.map((p) => (
              <li key={p.piid + p.account + p.program}>
                <a className="lrow" href={p.url} target="_blank" rel="noopener noreferrer">
                  <span className="lrow-main">
                    <b>{p.account}</b>
                    {p.program ? <span className="sub">{p.program}</span> : null}
                    <span className="sub">
                      {p.piid} · {companyName(p.recipient)}
                    </span>
                  </span>
                  <span className="lrow-side">
                    <span className="sub">USAspending ↗</span>
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </>
  );
}

function Files({ files, docs }: { files: NoticeFile[]; docs: [string, string, string][] }) {
  const rows: [string, string, ReactNode][] = [
    ...files.map((f) => [f.name, f.url, [f.size ? fileSize(f.size) : "", f.posted ? `posted ${date(f.posted)}` : ""].filter(Boolean).join(" · ")] as [string, string, string]),
    ...docs.map(([label, url, h]) => [label, url, h] as [string, string, string]),
  ];
  return (
    <section>
      <h4>Files and links</h4>
      <div className="docs files">
        {rows.map(([name, url, meta]) => (
          <a key={url} href={url} target="_blank" rel="noopener noreferrer">
            {name}
            <span>{meta}</span>
          </a>
        ))}
      </div>
    </section>
  );
}

export interface NoticeFile {
  name: string;
  url: string;
  original?: string; // a file GovChew serves (ATI members' FileCloud): its link at the source
  size: number | null;
  posted: string | null;
}

function fileSize(bytes: number) {
  return bytes >= 1 << 20 ? `${(bytes / (1 << 20)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
